import { useState, useEffect, useCallback, useRef } from "react";
import type { EnrichedProject } from "../types/index.js";
import { apiGet } from "../lib/api.js";

interface UseProjectsReturn {
	projects: EnrichedProject[];
	selectedProjectId: string | null;
	loading: boolean;
	error: string | null;
	selectProject: (id: string | null) => void;
	/** Re-fetches the projects list on demand (e.g. after a folder link). */
	refreshProjects: () => void;
}

/**
 * Projects hook.
 *
 * Fetches the list of enriched Zoho Projects when `authenticated` is true.
 * Provides project selection with toggle behaviour (clicking the already-selected
 * project deselects it).
 *
 * Persists the selected project ID to localStorage, scoped by zohoUserId, so the
 * selection survives page refreshes. The localStorage restore fires once on initial
 * authenticated load; subsequent calls to selectProject are treated as active user
 * switches.
 */
export function useProjects(authenticated: boolean, zohoUserId?: string): UseProjectsReturn {
	const [projects, setProjects] = useState<EnrichedProject[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Track whether the initial localStorage restore has occurred.
	// This distinguishes a page-load restore from an active user switch.
	const restoredRef = useRef(false);

	// Counter incremented by refreshProjects() to trigger a re-fetch.
	const [fetchTick, setFetchTick] = useState(0);

	// Restore selected project from localStorage on initial authenticated load
	useEffect(() => {
		if (authenticated && zohoUserId && !restoredRef.current) {
			restoredRef.current = true;
			const stored = localStorage.getItem(`pia_selected_project_${zohoUserId}`);
			if (stored) {
				setSelectedProjectId(stored);
			}
		}
	}, [authenticated, zohoUserId]);

	// Persist selected project to localStorage whenever it changes
	useEffect(() => {
		if (!zohoUserId) return;

		if (selectedProjectId) {
			localStorage.setItem(`pia_selected_project_${zohoUserId}`, selectedProjectId);
		} else {
			localStorage.removeItem(`pia_selected_project_${zohoUserId}`);
		}
	}, [selectedProjectId, zohoUserId]);

	useEffect(() => {
		if (!authenticated) {
			setProjects([]);
			setSelectedProjectId(null);
			restoredRef.current = false;
			return;
		}

		let cancelled = false;

		async function fetchProjects(): Promise<void> {
			setLoading(true);
			setError(null);

			try {
				const response = await apiGet("/api/projects");

				if (cancelled) return;

				if (response.ok) {
					const data = await response.json() as { projects: EnrichedProject[] };
					setProjects(data.projects);
				} else {
					const errorData = await response.json().catch(() => null) as { message?: string } | null;
					setError(errorData?.message || `Failed to load projects (${response.status})`);
				}
			} catch {
				if (!cancelled) {
					setError("Failed to connect to the server");
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		}

		fetchProjects();

		return () => {
			cancelled = true;
		};
	}, [authenticated, fetchTick]);

	const selectProject = useCallback((id: string | null) => {
		// Toggle behaviour: clicking the same project deselects it
		setSelectedProjectId((current) => (current === id ? null : id));
	}, []);

	/** Trigger a fresh fetch of the projects list. */
	const refreshProjects = useCallback(() => {
		setFetchTick((n) => n + 1);
	}, []);

	return { projects, selectedProjectId, loading, error, selectProject, refreshProjects };
}
